// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Bill findings content renderer (monitor analysis).
 * Used by BillRendererDialog to display score + impact areas.
 */
import type { FC } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'

import type { BillFindings, ImpactScore } from '../../models/types'

function getScoreBadgeVariant(scoreType: ImpactScore['scoreType']): 'default' | 'destructive' | 'secondary' {
  switch (scoreType) {
    case 'positive':
      return 'default'
    case 'negative':
      return 'destructive'
    case 'neutral':
      return 'secondary'
  }
}

function ScoreIcon({ scoreType }: { scoreType: ImpactScore['scoreType'] }) {
  switch (scoreType) {
    case 'positive':
      return <TrendingUp className="h-4 w-4" />
    case 'negative':
      return <TrendingDown className="h-4 w-4" />
    case 'neutral':
      return <Minus className="h-4 w-4" />
  }
}

function ImpactScoreDisplay({ label, score }: { label: string; score: ImpactScore }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <Badge variant={getScoreBadgeVariant(score.scoreType)} className="gap-1">
          <ScoreIcon scoreType={score.scoreType} />
          {score.score}/10
        </Badge>
      </div>
    </div>
  )
}

/** Content renderer for bill-findings component type */
export const BillFindingsContent: FC<{ data: unknown }> = ({ data }) => {
  const findings = data as BillFindings

  if (!findings?.reportOverview?.filterScore) {
    return null
  }

  return (
    <div className="space-y-6 py-4">
      {/* Overall Impact Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>Overall Impact</span>
            <Badge variant={getScoreBadgeVariant(findings.reportOverview.filterScore.scoreType)} className="gap-1">
              <ScoreIcon scoreType={findings.reportOverview.filterScore.scoreType} />
              {findings.reportOverview.filterScore.score}/10
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{findings.reportOverview.explanation}</p>
        </CardContent>
      </Card>

      {/* Impact Areas */}
      {findings.impactAreas && findings.impactAreas.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Impact Areas</h3>
          {findings.impactAreas.map((area, index) => (
            <Card key={index}>
              <CardHeader>
                <CardTitle className="text-base">{area.areaName}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <ImpactScoreDisplay label="Direct Impact" score={area.directImpact} />
                  <ImpactScoreDisplay label="Indirect Impact" score={area.indirectImpact} />
                </div>
                <div className="pt-2 border-t">
                  <p className="text-sm font-medium mb-1">Rationale</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{area.rationale}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
